import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from './Navigation';

const API_BASE = process.env.REACT_APP_API_URL || '/api/v2';

export default function PhoneVerification() {
  const { user, authFetch } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState('enter_phone'); // enter_phone | enter_otp | verified
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [countdown, setCountdown] = useState(0);

  const checkStatus = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/phone/status`);
      const data = await res.json();
      if (data && data.phone_verified) {
        try { const u = JSON.parse(localStorage.getItem('user') || '{}'); u.phone_verified = true; u.phoneVerified = true; localStorage.setItem('user', JSON.stringify(u)); } catch (e) {}
      setStep('verified');
        setTimeout(() => navigate('/dashboard'), 2000);
      }
      if (data && data.phone_number) {
        setPhoneNumber(data.phone_number);
      }
    } catch (err) {
      // not verified yet, stay on enter_phone
    }
  }, [authFetch, navigate]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleSendOtp = async () => {
    setError('');
    setMessage('');

    if (!phoneNumber.trim()) {
      setError('Please enter your phone number.');
      return;
    }

    // Auto-format to E.164 for North American numbers (sender)
    let formatted = phoneNumber.trim().replace(/[\s()-]/g, '');
    if (formatted.startsWith('+')) {
      // already E.164, keep as-is
    } else if (formatted.length === 10) {
      // 4165551234 -> +14165551234
      formatted = '+1' + formatted;
    } else if (formatted.length === 11 && formatted.startsWith('1')) {
      // 14165551234 -> +14165551234
      formatted = '+' + formatted;
    } else {
      formatted = '+' + formatted;
    }

    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/phone/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: formatted }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send OTP');
      setPhoneNumber(formatted);
      setStep('enter_otp');
      setMessage(`Verification code sent to ${formatted}`);
      setCountdown(60);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    setMessage('');

    if (!otp.trim() || otp.length !== 6) {
      setError('Please enter the 6-digit code.');
      return;
    }

    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/phone/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ otp: otp.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Verification failed');
      try { const u = JSON.parse(localStorage.getItem('user') || '{}'); u.phone_verified = true; u.phoneVerified = true; localStorage.setItem('user', JSON.stringify(u)); } catch (e) {}
      setStep('verified');
      setMessage('Phone verified successfully!');
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;
    setStep('enter_phone');
    setOtp('');
    setError('');
    setMessage('');
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)' }}>
      <Navigation />
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: 'calc(100vh - 64px)', padding: '20px'
      }}>
        <div style={{
          background: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '16px',
          padding: '40px',
          width: '100%',
          maxWidth: '420px',
          backdropFilter: 'blur(10px)',
        }}>

          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>
              {step === 'verified' ? '✅' : '📱'}
            </div>
            <h2 style={{ color: '#ffffff', fontSize: '24px', fontWeight: '700', margin: '0 0 8px' }}>
              {step === 'verified' ? 'Phone Verified!' : 'Verify Your Phone'}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px', margin: 0 }}>
              {step === 'enter_phone' && 'Enter your mobile number to receive a verification code'}
              {step === 'enter_otp' && `Enter the 6-digit code sent to ${phoneNumber}`}
              {step === 'verified' && 'Your phone number has been verified. Redirecting...'}
            </p>
          </div>

          {/* Progress dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '32px' }}>
            {['enter_phone', 'enter_otp', 'verified'].map((s, i) => (
              <div key={s} style={{
                width: '8px', height: '8px', borderRadius: '50%',
                background: ['enter_phone', 'enter_otp', 'verified'].indexOf(step) >= i
                  ? '#6c63ff' : 'rgba(255,255,255,0.2)',
                transition: 'background 0.3s'
              }} />
            ))}
          </div>

          {/* Error / Success messages */}
          {error && (
            <div style={{
              background: 'rgba(255,59,48,0.15)', border: '1px solid rgba(255,59,48,0.3)',
              borderRadius: '8px', padding: '12px 16px', marginBottom: '20px',
              color: '#ff6b6b', fontSize: '14px'
            }}>
              {error}
            </div>
          )}
          {message && !error && (
            <div style={{
              background: 'rgba(52,199,89,0.15)', border: '1px solid rgba(52,199,89,0.3)',
              borderRadius: '8px', padding: '12px 16px', marginBottom: '20px',
              color: '#34c759', fontSize: '14px'
            }}>
              {message}
            </div>
          )}

          {/* Step: Enter Phone */}
          {step === 'enter_phone' && (
            <div>
              <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', fontWeight: '500', display: 'block', marginBottom: '8px' }}>
                Phone Number
              </label>
              <input
                type="tel"
                placeholder="+1 416 555 1234"
                value={phoneNumber}
                onChange={e => setPhoneNumber(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSendOtp()}
                style={{
                  width: '100%', padding: '14px 16px', borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.08)', color: '#ffffff',
                  fontSize: '16px', outline: 'none', boxSizing: 'border-box',
                  marginBottom: '20px'
                }}
              />
              <button
                onClick={handleSendOtp}
                disabled={loading}
                style={{
                  width: '100%', padding: '14px', borderRadius: '10px',
                  background: loading ? 'rgba(108,99,255,0.5)' : '#6c63ff',
                  color: '#ffffff', fontSize: '16px', fontWeight: '600',
                  border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {loading ? 'Sending...' : 'Send Verification Code'}
              </button>
            </div>
          )}

          {/* Step: Enter OTP */}
          {step === 'enter_otp' && (
            <div>
              <label style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', fontWeight: '500', display: 'block', marginBottom: '8px' }}>
                Verification Code
              </label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => e.key === 'Enter' && handleVerifyOtp()}
                style={{
                  width: '100%', padding: '14px 16px', borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  background: 'rgba(255,255,255,0.08)', color: '#ffffff',
                  fontSize: '24px', fontWeight: '700', letterSpacing: '8px',
                  textAlign: 'center', outline: 'none', boxSizing: 'border-box',
                  marginBottom: '20px'
                }}
              />
              <button
                onClick={handleVerifyOtp}
                disabled={loading}
                style={{
                  width: '100%', padding: '14px', borderRadius: '10px',
                  background: loading ? 'rgba(108,99,255,0.5)' : '#6c63ff',
                  color: '#ffffff', fontSize: '16px', fontWeight: '600',
                  border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                  marginBottom: '16px', transition: 'all 0.2s'
                }}
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </button>

              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={handleResend}
                  disabled={countdown > 0}
                  style={{
                    background: 'none', border: 'none',
                    color: countdown > 0 ? 'rgba(255,255,255,0.3)' : '#6c63ff',
                    fontSize: '14px', cursor: countdown > 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  {countdown > 0 ? `Resend code in ${countdown}s` : 'Resend code'}
                </button>
              </div>
            </div>
          )}

          {/* Step: Verified */}
          {step === 'verified' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
                Redirecting to dashboard...
              </div>
            </div>
          )}

          {/* Skip for now */}
          {step !== 'verified' && (
            <div style={{ textAlign: 'center', marginTop: '24px' }}>
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  background: 'none', border: 'none',
                  color: 'rgba(255,255,255,0.4)', fontSize: '13px', cursor: 'pointer'
                }}
              >
                Skip for now
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
